import React, {useRef, useCallback, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../theme/ThemeProvider";
import BaseChart from "./BaseChart";
import Container from "../layout/Container";

const DonutChart = ({data = [], settings = {}, ...rest}) => {
  const {theme} = useTheme();

  const chartSettings = useMemo(
    () => ({
      innerRadius: 0.6,
      outerRadius: 1.0,
      ...settings,
    }),
    [settings],
  );

  const ChartRenderer = ({
    canvasRef,
    containerRef,
    chartSettings,
    hovering,
    setTooltipData,
    setTooltipPosition,
  }) => {
    const {theme} = useTheme();
    const segmentRefs = useRef([]);

    const drawChart = useCallback(() => {
      if (!canvasRef.current || !containerRef.current || data.length === 0)
        return;

      const containerWidth = containerRef.current.clientWidth || 0;
      const containerHeight = chartSettings.height || 300;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", {alpha: true});

      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;

      ctx.scale(dpr, dpr);
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const total = data.reduce((sum, item) => sum + item.value, 0);

      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;
      const maxRadius = Math.min(containerWidth, containerHeight) / 2 - 10;
      const outerRadius = maxRadius * chartSettings.outerRadius;
      const innerRadius = outerRadius * chartSettings.innerRadius;

      let startAngle = -Math.PI / 2; // Start at the top (12 o'clock)
      segmentRefs.current = [];

      data.forEach((item) => {
        const segmentAngle = (item.value / total) * (Math.PI * 2);
        const endAngle = startAngle + segmentAngle;

        ctx.beginPath();
        ctx.arc(centerX, centerY, outerRadius, startAngle, endAngle);
        ctx.arc(centerX, centerY, innerRadius, endAngle, startAngle, true);
        ctx.closePath();

        ctx.fillStyle = item.color;
        ctx.fill();

        const midAngle = startAngle + segmentAngle / 2;
        segmentRefs.current.push({
          startAngle,
          endAngle,
          midAngle,
          dataPoint: item,
          value: (item.value / total) * 100,
        });

        startAngle = endAngle;
      });
    }, [canvasRef, containerRef, data, chartSettings, theme]);

    const handleMouseMove = useCallback(
      (event) => {
        if (!hovering || !canvasRef.current || !containerRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Calculate center and radius
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const outerRadius = Math.min(rect.width, rect.height) / 2 - 10;

        // Check if mouse is within chart area (entire chart, not just donut)
        const distanceFromCenter = Math.sqrt(
          Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2),
        );

        if (distanceFromCenter < outerRadius * 1.1) {
          const total = data.reduce((sum, item) => sum + item.value, 0);

          setTooltipData({
            items: data.map((item) => ({
              label: item.display || item.id || "Unknown",
              value: `${((item.value / total) * 100).toFixed(1)}% - ${item.value.toLocaleString()} items`,
              color: item.color,
            })),
          });

          setTooltipPosition({
            x: centerX,
            y: centerY - outerRadius / 2,
          });
        } else {
          setTooltipData(null);
        }
      },
      [
        canvasRef,
        containerRef,
        hovering,
        setTooltipData,
        setTooltipPosition,
        data,
      ],
    );

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const boundMouseMove = (e) => handleMouseMove(e);
      canvas.addEventListener("mousemove", boundMouseMove);

      return () => {
        canvas.removeEventListener("mousemove", boundMouseMove);
      };
    }, [handleMouseMove]);

    useEffect(() => {
      if (canvasRef.current && containerRef.current && data.length > 0) {
        drawChart();
      }
    }, [drawChart, data]);

    useEffect(() => {
      const handleResize = () => {
        if (canvasRef.current && containerRef.current && data.length > 0) {
          drawChart();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [drawChart, data.length]);

    return (
      <>
        {chartSettings.centerContent?.element && (
          <Container
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            {chartSettings.centerContent.element}
          </Container>
        )}
      </>
    );
  };

  return (
    <BaseChart
      data={data}
      settings={{
        height: 200,
        padding: {top: 0, right: 0, bottom: 0, left: 0},
        ...chartSettings,
      }}
      renderer={ChartRenderer}
      {...rest}
    />
  );
};

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      display: PropTypes.string,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  settings: PropTypes.object,
};

export default DonutChart;
